import uniqBy from 'lodash/uniqBy';
import isArray from 'lodash/isArray';
import { generateCheckedItemsInRange, isCheckedResultInRange } from '@Containers/PatentFamilyClustering/utils';
import {
  INIT_RESULTS_STATE,
  SET_CHECKED_ITEMS,
  SET_RESULTS_PER_PAGE,
  FETCH_RESULTS_ITEMS,
  FETCH_RESULTS_ITEMS_SUCCESS,
  FETCH_RESULTS_ITEMS_FAILURE,
  ADD_RESULTS_CHUNK,
  SET_ALL_RESULTS_CHECKED,
  SET_ALL_RESULTS_UNCHECKED,
  SET_RESULT_CHECKED,
  SET_PRODUCT_CHECKED,
  SET_PRODUCTS_CHECKED_FOR_SUBSTANCE,
  CLEAN_RESULTS_STATE,
} from './constants';
import { mapResultItemToChecked, mapProductToChecked, mapAllResultsToChecked } from './utils';

export const initialState = {
  resultsPerPage: null,
  resultsFinished: false,
  resultsInProgress: 0,
  resultsItems: [],
  requestedParams: {}, // for last loaded results
  checkedItems: {
    results: [], // expected item format: { index, id }, additionally for patent family clustering: { absoluteIndex, relativeIndex }
    products: [], // expect item format: { productIndex, productId, substanceIndex, substanceId }
  },
};

// eslint-disable-next-line default-param-last
export default function reducer(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case INIT_RESULTS_STATE: {
      const { params, checkedItems } = payload;
      return {
        ...state,
        requestedParams: params,
        ...(checkedItems
          ? { checkedItems }
          : {
              checkedItems: initialState.checkedItems,
            }),
      };
    }
    case SET_CHECKED_ITEMS: {
      const { checkedItems } = payload;
      if (!checkedItems) {
        return state;
      }
      return {
        ...state,
        checkedItems,
      };
    }
    case CLEAN_RESULTS_STATE: {
      return {
        ...state,
        resultsItems: initialState.resultsItems,
        totalResults: initialState.totalResults,
        resultsFinished: false,
        checkedItems: initialState.checkedItems,
      };
    }
    case SET_RESULTS_PER_PAGE:
      return {
        ...state,
        resultsPerPage: payload,
      };
    case FETCH_RESULTS_ITEMS:
      return {
        ...state,
        resultsItems: [],
        resultsInProgress: state.resultsInProgress + 1,
        resultsFinished: false,
      };
    case FETCH_RESULTS_ITEMS_SUCCESS:
    case FETCH_RESULTS_ITEMS_FAILURE:
      return {
        ...state,
        resultsInProgress: state.resultsInProgress > 1 ? state.resultsInProgress - 1 : 0,
        resultsFinished: state.resultsInProgress <= 1,
      };
    case ADD_RESULTS_CHUNK: {
      const { resultsItems } = state;
      return {
        ...state,
        resultsItems: uniqBy(resultsItems.concat(payload), ({ index }) => index),
      };
    }
    case SET_ALL_RESULTS_CHECKED: {
      const { checked } = payload;
      const { resultsItems, checkedItems } = state;
      const { results: checkedResults, products: checkedProducts } = checkedItems;
      const mappedResultsToChecked = mapAllResultsToChecked(resultsItems);

      return {
        ...state,
        checkedItems: {
          ...checkedItems,
          results: checkedResults
            .filter(({ index: checkedResultIndex, absoluteIndex: checkedResultAbsoluteIndex }) => {
              // Patent Family Clustering
              if (checkedResultAbsoluteIndex) {
                return !mappedResultsToChecked.find(
                  ({ absoluteIndex: resultAbsoluteIndex }) => resultAbsoluteIndex === checkedResultAbsoluteIndex
                );
              }

              // Generic results
              return !mappedResultsToChecked.find(({ index: resultIndex }) => resultIndex === checkedResultIndex);
            })
            .concat(checked ? mappedResultsToChecked : []),
          products: checkedProducts.filter(
            ({ substanceIndex: checkedSubstanceIndex }) =>
              !resultsItems.find(({ index: resultIndex }) => resultIndex === checkedSubstanceIndex)
          ),
        },
      };
    }
    case SET_ALL_RESULTS_UNCHECKED: {
      return {
        ...state,
        checkedItems: initialState.checkedItems,
      };
    }

    case SET_RESULT_CHECKED: {
      const { item, checked } = payload;
      const { index, absoluteIndex } = item;
      const { checkedItems } = state;
      const { results: checkedResults, products: checkedProducts } = checkedItems;

      let newCheckedResults;

      if (!absoluteIndex) {
        // Generic results
        newCheckedResults = checkedResults
          .filter(({ index: checkedResultIndex }) => checkedResultIndex !== index)
          .concat(checked ? [mapResultItemToChecked(item)] : []);
      } else if (isArray(absoluteIndex)) {
        // Patent Family Clustering - main result item
        newCheckedResults = checkedResults
          .filter((checkedResult) => !isCheckedResultInRange(checkedResult, absoluteIndex))
          .concat(checked ? generateCheckedItemsInRange(absoluteIndex, index) : []);
      } else {
        // Patent Family Clustering - single patents or family members
        newCheckedResults = checkedResults
          .filter(({ absoluteIndex: checkedResultAbsoluteIndex }) => checkedResultAbsoluteIndex !== absoluteIndex)
          .concat(checked ? [mapResultItemToChecked(item)] : []);
      }

      return {
        ...state,
        checkedItems: {
          ...checkedItems,
          results: newCheckedResults,
          products: checkedProducts.filter(({ substanceIndex: checkedSubstanceIndex }) => checkedSubstanceIndex !== index),
        },
      };
    }
    case SET_PRODUCT_CHECKED: {
      const { product, checked } = payload;
      const { productIndex } = product;
      const { checkedItems } = state;
      const { products: checkedProducts } = checkedItems;

      return {
        ...state,
        checkedItems: {
          ...checkedItems,
          products: checkedProducts
            .filter(({ productIndex: checkedProductIndex }) => checkedProductIndex !== productIndex)
            .concat(checked ? [mapProductToChecked(product)] : []),
        },
      };
    }
    case SET_PRODUCTS_CHECKED_FOR_SUBSTANCE: {
      const { products, substanceIndex } = payload;
      const { checkedItems } = state;
      const { results: checkedResults, products: checkedProducts } = checkedItems;

      return {
        ...state,
        checkedItems: {
          ...checkedItems,
          results: checkedResults.filter(({ index: checkedResultIndex }) => checkedResultIndex !== substanceIndex),
          products: checkedProducts
            .filter(({ substanceIndex: checkedSubstanceIndex }) => checkedSubstanceIndex !== substanceIndex)
            .concat(products.map((product) => mapProductToChecked(product))),
        },
      };
    }
    default:
      return state;
  }
}
